import React, { useContext } from 'react';
import { Modal, Image, Button, Divider, Space, message } from 'antd';
import dayjs from 'dayjs';
import { NotificationOutlined } from '@ant-design/icons';
import client from '@/lib/feathers';
import useService from '@/lib/hooks/useService';
import { GroupBuyingOrdersContext } from '../SettingAndManagement';
import styles from './index.module.scss';
import type { AddOrderFormDataType } from '../types';
import type { CommunityData } from '@/types/database/communityData';

interface PreviewForGroupOrderProps {
  open: boolean;
  order: AddOrderFormDataType;
  closePreviewForGroupOrderModal: () => void;
  closeAllModalAfterAddOrEditGroupBuying: () => void;
}

const iconStyle = {
  fontSize: '17px',
  color: '#209BFA',
};

const PreviewForGroupOrder = ({
  open,
  order,
  closePreviewForGroupOrderModal,
  closeAllModalAfterAddOrEditGroupBuying,
}: PreviewForGroupOrderProps) => {
  const [communityData] = useService<CommunityData>({
    collectionName: 'communityData',
  });

  const {
    itemName,
    status,
    expirationTime,
    allergen,
    storageMethod,
    manufacturerName,
    manufacturerPhone,
    manufacturerAddress,
    itemIntroduction,
    itemSpec,
    notice,
    uploadedImage,
    creditCardCost,
    pointsCost,
    maximumAmount,
    establishTime,
    startTime,
    endTime,
  } = order;

  const groupBuyingService = client.service('groupBuying');

  const { updateGroupBuyingOrder } = useContext(GroupBuyingOrdersContext);

  const getClosingTime = () => {
    const { closingOrderNumber, closingOrderUnit } =
      communityData[0].groupBuying;
    switch (closingOrderUnit) {
      case '天':
        return dayjs(endTime).subtract(closingOrderNumber, 'day').unix();
      case '週':
        return dayjs(endTime).subtract(closingOrderNumber, 'week').unix();
      default:
        return dayjs(endTime).subtract(closingOrderNumber, 'month').unix();
    }
  };

  const addOrUpdateGroupBuyingOrder = async (
    actionType: 'create' | 'update'
  ) => {
    if (actionType === 'update') {
      const { id, uploadedImage, ...others } = order;
      await groupBuyingService.patch(id, {
        ...others,
        imgUrl: uploadedImage[0].showPath,
        cost: [
          {
            type: '信用卡',
            amount: creditCardCost ? creditCardCost : '',
          },
          {
            type: '點數',
            amount: pointsCost ? pointsCost : '',
          },
        ],
        establishTime: dayjs(establishTime).unix(),
        startTime: dayjs(startTime).unix(),
        endTime: dayjs(endTime).unix(),
        closingTime: getClosingTime(),
        note: '',
      });
    } else {
      const { uploadedImage, ...others } = order;
      await groupBuyingService.create({
        ...others,
        status: '待上架',
        imgUrl:
          uploadedImage.length === 0 ? '/ezcon.png' : uploadedImage[0].showPath,
        cost: [
          {
            type: '信用卡',
            amount: creditCardCost ? creditCardCost : '',
          },
          {
            type: '點數',
            amount: pointsCost ? pointsCost : '',
          },
        ],
        establishTime: dayjs(establishTime).unix(),
        startTime: dayjs(startTime).unix(),
        endTime: dayjs(endTime).unix(),
        closingTime: getClosingTime(),
        note: '',
      });
    }
    updateGroupBuyingOrder();
    closeAllModalAfterAddOrEditGroupBuying();
    status !== undefined
      ? message.success('團購更新成功!')
      : message.success('團購建立成功!');
  };

  return (
    <Modal
      open={open}
      onCancel={closePreviewForGroupOrderModal}
      footer={[
        <Button
          key="back"
          type="primary"
          onClick={closePreviewForGroupOrderModal}
        >
          修改
        </Button>,
        <Button
          key="submit"
          onClick={() => {
            status !== undefined
              ? addOrUpdateGroupBuyingOrder('update')
              : addOrUpdateGroupBuyingOrder('create');
          }}
        >
          確認
        </Button>,
      ]}
    >
      <Image
        width="100%"
        src={
          uploadedImage && uploadedImage.length > 0
            ? uploadedImage[0].showPath
            : '/ezcon.png'
        }
        preview={false}
      />
      <Divider />
      <h1>{itemName}</h1>
      <Divider />
      <Space direction="vertical">
        <Space>
          <NotificationOutlined style={iconStyle} />
          <h4 className={styles.sectionTitle}>團購資訊：</h4>
        </Space>
        <div>
          <h5 className={styles.contentTitle}>團購起始時間</h5>
          <p>{dayjs(startTime).format('YYYY/MM/DD HH:mm')}</p>
        </div>
        <div>
          <h5 className={styles.contentTitle}>團購截止時間</h5>
          <p>{dayjs(endTime).format('YYYY/MM/DD HH:mm')}</p>
        </div>
      </Space>
      <Divider />
      <Space direction="vertical">
        <Space>
          <NotificationOutlined style={iconStyle} />
          <h4 className={styles.sectionTitle}>商品資訊：</h4>
        </Space>
        <div>
          <h5 className={styles.contentTitle}>賞味期限</h5>
          <p>{expirationTime}</p>
        </div>
        <div>
          <h5 className={styles.contentTitle}>過敏原</h5>
          <p>{allergen}</p>
        </div>
        <div>
          <h5 className={styles.contentTitle}>保存方法</h5>
          <p>{storageMethod}</p>
        </div>
        <div>
          <h5 className={styles.contentTitle}>廠商名稱</h5>
          <p>{manufacturerName}</p>
        </div>
        <div>
          <h5 className={styles.contentTitle}>廠商電話</h5>
          <p>{manufacturerPhone}</p>
        </div>
        <div>
          <h5 className={styles.contentTitle}>廠商地址</h5>
          <p>{manufacturerAddress}</p>
        </div>
      </Space>
      <Divider />
      <Space direction="vertical">
        <Space>
          <NotificationOutlined style={iconStyle} />
          <h4 className={styles.sectionTitle}>商品資訊：</h4>
        </Space>
        <div>
          <h5 className={styles.contentTitle}>商品介紹</h5>
          <p>{itemIntroduction}</p>
        </div>
        <div>
          <h5 className={styles.contentTitle}>商品包含</h5>
          <p>{itemSpec}</p>
        </div>
      </Space>
      <Divider />
      <Space direction="vertical">
        <Space>
          <NotificationOutlined style={iconStyle} />
          <h4 className={styles.sectionTitle}>注意事項：</h4>
        </Space>
        <div>
          <h5 className={styles.contentTitle}>注意事項</h5>
          <p>{notice}</p>
        </div>
      </Space>
      <Divider />
      <Space direction="vertical" className={styles.orderPlanWrapper}>
        <Space>
          <NotificationOutlined style={iconStyle} />
          <h4 className={styles.sectionTitle}>團購方案：</h4>
        </Space>
        <div className={styles.planInfoContainer}>
          <div>
            <h5 className={styles.contentTitle}>方案</h5>
            <p>{itemName}</p>
          </div>
          <div>
            <h5 className={styles.contentTitle}>價格</h5>
            <p>
              {creditCardCost ? `NT$ ${creditCardCost}` : `${pointsCost}點`}
            </p>
          </div>
          <div>
            <h5 className={styles.contentTitle}>數量</h5>
            <p>{maximumAmount}</p>
          </div>
        </div>
      </Space>
    </Modal>
  );
};

export default PreviewForGroupOrder;
